import { useState, useRef } from "react";
import { Plus, Edit2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectManager = ({ projects, token, credentials, onRefresh }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "doors",
    image_url: "",
    location: "",
    featured: false,
  });

  // Get auth config (JWT or Basic Auth fallback)
  const getAuthConfig = () => {
    if (token) {
      return { headers: { Authorization: `Bearer ${token}` } };
    }
    if (credentials?.username && credentials?.password) {
      return { auth: { username: credentials.username, password: credentials.password } };
    }
    return {};
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPG, PNG, WebP, or GIF");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      // Use JWT token for authentication
      const authConfig = getAuthConfig();
      const response = await axios.post(`${API}/upload`, uploadData, {
        ...authConfig,
        headers: { 
          ...authConfig.headers,
          "Content-Type": "multipart/form-data" 
        },
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded successfully!");
    } catch {
      toast.error("Failed to upload image");
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const openDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        description: project.description,
        category: project.category,
        image_url: project.image_url,
        location: project.location || "",
        featured: project.featured || false,
      });
      setImagePreview(project.image_url);
    } else {
      setEditingProject(null);
      setFormData({
        title: "",
        description: "",
        category: "doors",
        image_url: "",
        location: "",
        featured: false,
      });
      setImagePreview(null);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const config = getAuthConfig();
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData, config);
        toast.success("Project updated!");
      } else {
        await axios.post(`${API}/projects`, formData, config);
        toast.success("Project created!");
      }
      setIsDialogOpen(false);
      setImagePreview(null);
      onRefresh();
    } catch {
      toast.error("Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      const config = getAuthConfig();
      await axios.delete(`${API}/projects/${id}`, config);
      toast.success("Project deleted!");
      onRefresh();
    } catch {
      toast.error("Failed to delete project");
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0A0A0A]">
          Projects ({projects.length})
        </h2>
        <button
          onClick={() => openDialog()}
          className="bg-[#FF6B00] text-white px-4 py-2 flex items-center gap-2 hover:bg-[#E65000] transition-colors"
          data-testid="add-project-btn"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white border border-[#E5E5E5] overflow-hidden"
            data-testid={`project-card-${project.id}`}
          >
            <div className="aspect-video overflow-hidden bg-[#F5F5F5]">
              <img
                src={project.image_url}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-[#0A0A0A]">{project.title}</h3>
              <p className="text-sm text-[#525252] line-clamp-2">
                {project.description}
              </p>
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs bg-[#F5F5F5] px-2 py-1 uppercase">
                  {project.category}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => openDialog(project)}
                    className="p-2 text-[#525252] hover:text-[#FF6B00] transition-colors"
                    data-testid={`edit-project-${project.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    className="p-2 text-[#525252] hover:text-red-500 transition-colors"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">
              {editingProject ? "Edit Project" : "Add New Project"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#525252] mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#FF6B00] focus:outline-none"
                required
                data-testid="project-title-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#525252] mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#FF6B00] focus:outline-none"
                required
                data-testid="project-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-[#525252] mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#FF6B00] focus:outline-none"
                  data-testid="project-category-select"
                >
                  <option value="doors">Doors</option>
                  <option value="windows">Windows</option>
                  <option value="outbuilds">Garden Rooms</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-[#525252] mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="e.g., Motherwell"
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#FF6B00] focus:outline-none"
                  data-testid="project-location-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-[#525252] mb-2">
                Image
              </label>
              <div className="border-2 border-dashed border-[#E5E5E5] p-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData({ ...formData, image_url: "" });
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-col items-center justify-center h-48 cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                  >
                    <Upload className="w-8 h-8 text-[#525252] mb-2" />
                    <p className="text-sm text-[#525252]">
                      {uploading ? "Uploading..." : "Click to upload image"}
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="project-image-input"
                />
              </div>
              <input
                type="text"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData({ ...formData, image_url: e.target.value })
                }
                placeholder="Or paste image URL"
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#FF6B00] focus:outline-none mt-2"
                data-testid="project-image-url-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) =>
                  setFormData({ ...formData, featured: e.target.checked })
                }
                className="w-4 h-4"
                data-testid="project-featured-checkbox"
              />
              <label htmlFor="featured" className="text-sm text-[#525252]">
                Featured on homepage
              </label>
            </div>
            <button
              type="submit"
              disabled={loading || uploading}
              className="w-full bg-[#FF6B00] text-white font-bold uppercase tracking-wide py-4 hover:bg-[#E65000] transition-all disabled:opacity-50"
              data-testid="save-project-btn"
            >
              {loading ? "Saving..." : editingProject ? "Update Project" : "Create Project"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectManager;
